import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, BookOpen, RefreshCw, Save } from 'lucide-react';
import EnTeteSection from '../parametrage/EnTeteSection';
import CarteReglages from '../parametrage/CarteReglages';
import ChargementSection from '../parametrage/ChargementSection';
import { LigneReglage } from '../parametrage/PiecesReglage';
import ListeDeroulante from '../ui/ListeDeroulante';
import CaseACocher from '../ui/CaseACocher';
import { api } from '../../services/api';

/**
 * Onglet « Journal » de la page des reçus (décisions de Daniel du 04/11/2026).
 *
 * Le tiroir-caisse : chaque ouverture commandée par KLASYS, notée par le
 * serveur au moment où il prépare le ticket (RecuJournalService) - quand, par
 * qui, pour quel reçu et combien d'espèces, et ce que l'agent du poste a
 * répondu. Ce qu'il ne voit pas, dit à l'écran : une ouverture à la clé.
 *
 * Le papier thermique : chaque ticket imprimé est compté avec sa longueur ; à
 * partir du stock compté, la page dit combien de rouleaux et de jours il
 * reste, et prévient sous le seuil. Le stock s'enregistre à part (régime B,
 * PARAMETRAGE-MODULES.md §5) avec la porte des réglages ; le journal se lit
 * avec celle de la page.
 */

const ACCENT = '#3084b5';
const ACCENT_SOMBRE = '#246990';

const BOUTON_SECONDAIRE = 'flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-500 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40';
const CHAMP = 'h-9 rounded-lg border border-gray-200 px-3 text-sm focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/15';

const fcfa = (valeur) => `${Math.round(Number(valeur) || 0).toLocaleString('fr-FR')} FCFA`;
const metres = (mm) => `${(Math.round((Number(mm) || 0) / 100) / 10).toLocaleString('fr-FR')} m`;
const heure = (murale) => String(murale || '').slice(11, 16);
const jourCourt = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}` : '';
};
const aujourdhui = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const MOTIFS = [
  { valeur: '', libelle: 'Tous les motifs' },
  { valeur: 'recu', libelle: 'Reçu' },
  { valeur: 'duplicata', libelle: 'Duplicata' },
  { valeur: 'ticket_essai', libelle: "Ticket d'essai" },
  { valeur: 'essai_tiroir', libelle: 'Essai du tiroir' }
];

const VOIES = { agent: 'Agent', navigateur: 'Navigateur', aucune: 'Rien imprimé' };

/** Ce que le tiroir a fait, pour une ligne. */
const etatTiroir = (ligne) => {
  if (!ligne.tiroir_demande) return { texte: '-', classe: 'text-gray-400' };
  if (ligne.tiroir_resultat === 'ouvert') return { texte: 'Ouvert', classe: 'font-semibold text-[#246990]' };
  if (ligne.tiroir_resultat === 'non_ouvert') return { texte: 'Resté fermé', classe: 'font-semibold text-amber-700' };
  return { texte: 'Sans réponse du poste', classe: 'text-gray-500' };
};

const Compteur = ({ libelle, valeur, alerte = false }) => (
  <div className={`rounded-lg border px-4 py-3 ${alerte ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{libelle}</p>
    <p className={`mt-1 text-lg font-bold ${alerte ? 'text-amber-700' : 'text-gray-900'}`}>{valeur}</p>
  </div>
);

// ----------------------------------------------------------------------------
// Le tiroir-caisse et les sorties du jour
// ----------------------------------------------------------------------------

const JournalDuJour = ({ avertir }) => {
  const [jour, setJour] = useState(aujourdhui());
  const [employe, setEmploye] = useState('');
  const [motif, setMotif] = useState('');
  const [tiroirSeul, setTiroirSeul] = useState(true);
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    const reponse = await api.recusCaisse.journal({
      jour, employe_id: employe || null, motif: motif || null, tiroir: tiroirSeul ? '1' : null
    });
    setChargement(false);
    if (reponse?.success) setDonnees(reponse.data);
    else avertir(reponse?.message || 'Le journal n\'a pas pu être lu');
  }, [jour, employe, motif, tiroirSeul, avertir]);

  useEffect(() => { charger(); }, [charger]);

  const c = donnees?.compteurs || {};
  const employes = [{ valeur: '', libelle: 'Tous les caissiers' }, ...(donnees?.employes || []).map((e) => ({ valeur: String(e.id), libelle: e.nom || `Employé n° ${e.id}` }))];

  return (
    <CarteReglages titre="Tiroir-caisse et impressions du jour" accent={ACCENT} compteur={donnees ? `${c.ouvertures || 0} ouverture(s)` : undefined}>
      {donnees && !donnees.installe ? (
        <p className="text-sm text-gray-600">Le journal n'est pas encore installé sur cette école : il le sera au prochain démarrage du serveur.</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <input type="date" value={jour} max={aujourdhui()} onChange={(e) => setJour(e.target.value || aujourdhui())} className={CHAMP} aria-label="Jour" />
            <div className="w-56"><ListeDeroulante valeur={employe} onChange={setEmploye} options={employes} aria-label="Caissier" /></div>
            <div className="w-48"><ListeDeroulante valeur={motif} onChange={setMotif} options={MOTIFS} aria-label="Motif" /></div>
            <CaseACocher checked={tiroirSeul} onChange={setTiroirSeul} label="Ouvertures du tiroir seulement" className="rounded-lg border border-gray-200 py-2" />
            <button type="button" onClick={charger} disabled={chargement} className={`${BOUTON_SECONDAIRE} ml-auto`}>
              <RefreshCw size={13} className={chargement ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>

          {!donnees && <ChargementSection accent={ACCENT} libelle="Lecture du journal…" />}

          {donnees && (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Compteur libelle="Ouvertures du tiroir" valeur={c.ouvertures || 0} />
                <Compteur libelle="Dont hors reçu (essais)" valeur={c.ouvertures_hors_recu || 0} alerte={(c.ouvertures_hors_recu || 0) > 0} />
                <Compteur libelle="Espèces des reçus ouverts" valeur={fcfa(c.especes)} />
                <Compteur libelle="Reçus imprimés" valeur={`${c.imprimes || 0} (${metres(c.papier_mm)})`} />
              </div>
              {((c.ouvertures_sans_retour || 0) > 0 || (c.ouvertures_echouees || 0) > 0) && (
                <p className="text-xs text-gray-600">
                  {c.ouvertures_echouees || 0} commande(s) restée(s) sans ouverture (agent injoignable, reçu passé par le navigateur),
                  {' '}{c.ouvertures_sans_retour || 0} sans réponse du poste (page fermée avant la fin).
                </p>
              )}

              {donnees.lignes.length === 0 ? (
                <p className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                  Rien au journal pour ce jour avec ces filtres.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Heure</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Motif</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Reçu</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Caissier</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Voie</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Tiroir</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">Espèces</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donnees.lignes.map((l) => {
                        const t = etatTiroir(l);
                        return (
                          <tr key={l.id} className="border-t border-gray-100">
                            <td className="px-3 py-2 font-mono text-xs text-gray-700">{heure(l.le)}</td>
                            <td className="px-3 py-2 text-gray-700">{l.libelle_motif}</td>
                            <td className="px-3 py-2 text-gray-700">
                              {l.numero ? <span className="font-mono text-xs font-semibold text-gray-800">{l.numero}</span> : '-'}
                              {l.beneficiaire && <span className="ml-2 text-xs text-gray-500">{l.beneficiaire}</span>}
                            </td>
                            <td className="px-3 py-2 text-gray-700">{l.employe || '-'}</td>
                            <td className="px-3 py-2 text-gray-700" title={l.message || undefined}>{VOIES[l.voie] || 'Sans réponse'}</td>
                            <td className={`px-3 py-2 ${t.classe}`}>{t.texte}</td>
                            <td className="px-3 py-2 text-right text-gray-800">{l.tiroir_demande && l.montant_especes !== null ? fcfa(l.montant_especes) : '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-xs text-gray-500">
                Le journal note chaque ouverture que KLASYS commande : à l'encaissement, au ticket d'essai et au bouton « Essayer ».
                Une ouverture à la clé ne passe pas par KLASYS et n'y figure pas.
              </p>
            </>
          )}
        </div>
      )}
    </CarteReglages>
  );
};

// ----------------------------------------------------------------------------
// Le papier thermique
// ----------------------------------------------------------------------------

const PapierThermique = ({ avertir }) => {
  const [etat, setEtat] = useState(null);
  const [brouillon, setBrouillon] = useState({ longueur_rouleau_m: '', stock_rouleaux: '', seuil_jours: '' });
  const [recompte, setRecompte] = useState(false);
  const [saving, setSaving] = useState(false);

  const appliquer = useCallback((donnees) => {
    setEtat(donnees);
    setBrouillon({
      longueur_rouleau_m: donnees?.longueur_rouleau_m ?? '',
      stock_rouleaux: donnees?.stock_rouleaux ?? '',
      seuil_jours: donnees?.seuil_jours ?? 10
    });
    setRecompte(false);
  }, []);

  const charger = useCallback(async () => {
    const reponse = await api.recusCaisse.papier();
    if (reponse?.success) appliquer(reponse.data);
    else avertir(reponse?.message || 'Le papier n\'a pas pu être lu');
  }, [appliquer, avertir]);

  useEffect(() => { charger(); }, [charger]);

  const changer = (cle, valeur) => {
    setBrouillon((b) => ({ ...b, [cle]: valeur.replace(/[^\d]/g, '') }));
    if (cle === 'stock_rouleaux') setRecompte(true);
  };
  const hasChanges = Boolean(etat) && (recompte
    || String(brouillon.longueur_rouleau_m) !== String(etat.longueur_rouleau_m ?? '')
    || String(brouillon.seuil_jours) !== String(etat.seuil_jours ?? 10));

  const enregistrer = async () => {
    setSaving(true);
    const reponse = await api.recusCaisse.enregistrerPapier({
      longueur_rouleau_m: brouillon.longueur_rouleau_m === '' ? null : Number(brouillon.longueur_rouleau_m),
      stock_rouleaux: brouillon.stock_rouleaux === '' ? null : Number(brouillon.stock_rouleaux),
      seuil_jours: brouillon.seuil_jours === '' ? null : Number(brouillon.seuil_jours),
      recompte
    });
    setSaving(false);
    if (reponse?.success) {
      appliquer(reponse.data);
      avertir('Stock de papier enregistré', 'success');
    } else {
      avertir(reponse?.message || "Le stock n'a pas pu être enregistré");
    }
  };

  if (!etat) return <ChargementSection accent={ACCENT} libelle="Lecture du papier…" />;
  if (!etat.installe) {
    return (
      <CarteReglages titre="Papier thermique" accent={ACCENT}>
        <p className="text-sm text-gray-600">Le suivi du papier n'est pas encore installé sur cette école : il le sera au prochain démarrage du serveur.</p>
      </CarteReglages>
    );
  }

  const stockConnu = etat.rouleaux_restants !== null && etat.rouleaux_restants !== undefined;
  let resume = 'Indiquez la longueur d\'un rouleau et le nombre de rouleaux en stock pour suivre le papier.';
  if (stockConnu && etat.jours_restants !== null) {
    resume = `Il reste environ ${String(etat.rouleaux_restants).replace('.', ',')} rouleau(x), soit ${etat.jours_restants} jour(s) d'impression au rythme actuel.`;
  } else if (stockConnu) {
    resume = `Il reste environ ${String(etat.rouleaux_restants).replace('.', ',')} rouleau(x). Le rythme d'impression se calculera après les premiers reçus.`;
  }

  return (
    <CarteReglages titre="Papier thermique" accent={ACCENT} compteur={stockConnu ? `${String(etat.rouleaux_restants).replace('.', ',')} rouleau(x)` : undefined}>
      <div className="space-y-4">
        <div className={`flex items-start gap-3 rounded-lg border p-3 text-sm ${etat.alerte ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-[#246990]/20 bg-[#eef7fb] text-[#1f5a7a]'}`}>
          {etat.alerte && <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
          <div>
            <p className="font-semibold">{etat.alerte ? 'Papier bientôt épuisé : commandez des rouleaux.' : 'Stock de papier'}</p>
            <p className="mt-0.5">{resume}</p>
            {etat.compte_le && (
              <p className="mt-0.5 text-xs opacity-80">
                Compté le {jourCourt(etat.compte_le)} à {heure(etat.compte_le)} ; {metres(etat.consomme_mm)} imprimés depuis.
              </p>
            )}
          </div>
        </div>

        <div className="divide-y divide-gray-50 rounded-lg border border-gray-100">
          <LigneReglage accent={ACCENT} libelle="Longueur d'un rouleau" description="Inscrite sur le carton des rouleaux (par exemple 80 x 80 mm, 70 m).">
            <span className="flex items-center gap-2">
              <input value={brouillon.longueur_rouleau_m} onChange={(e) => changer('longueur_rouleau_m', e.target.value)} inputMode="numeric" className={`${CHAMP} w-24 text-center`} aria-label="Longueur d'un rouleau en mètres" />
              <span className="text-sm text-gray-500">m</span>
            </span>
          </LigneReglage>
          <LigneReglage accent={ACCENT} libelle="Rouleaux en stock" description="Comptez les rouleaux neufs et saisissez leur nombre : le suivi repart de ce comptage.">
            <input value={brouillon.stock_rouleaux} onChange={(e) => changer('stock_rouleaux', e.target.value)} inputMode="numeric" className={`${CHAMP} w-24 text-center`} aria-label="Rouleaux en stock" />
          </LigneReglage>
          <LigneReglage accent={ACCENT} libelle="Prévenir quand il reste" description="La page et les écrans de caisse le signalent sous ce nombre de jours d'impression.">
            <span className="flex items-center gap-2">
              <input value={brouillon.seuil_jours} onChange={(e) => changer('seuil_jours', e.target.value)} inputMode="numeric" className={`${CHAMP} w-20 text-center`} aria-label="Seuil d'alerte en jours" />
              <span className="text-sm text-gray-500">jours</span>
            </span>
          </LigneReglage>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <p className="text-xs text-gray-500">
            {etat.jours_actifs
              ? `${etat.tickets_par_jour} reçu(s) et ${metres(etat.moyenne_mm_par_jour)} par jour d'activité (sur ${etat.jours_actifs} jour(s) des 30 derniers).`
              : 'Aucun ticket 80 mm imprimé ces 30 derniers jours.'}
            {' '}Longueurs estimées pour l'agent, mesurées par le navigateur.
          </p>
          <button
            type="button"
            onClick={enregistrer}
            disabled={!hasChanges || saving}
            className={`ml-auto flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all ${hasChanges && !saving ? 'hover:shadow-md' : 'cursor-not-allowed bg-gray-300 shadow-none'}`}
            style={hasChanges && !saving ? { backgroundColor: ACCENT_SOMBRE } : undefined}
          >
            <Save size={13} /> {saving ? 'Enregistrement…' : 'Enregistrer le stock'}
          </button>
        </div>

        {etat.par_jour?.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Jour</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Reçus imprimés</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Papier</th>
                </tr>
              </thead>
              <tbody>
                {etat.par_jour.slice(0, 14).map((j) => (
                  <tr key={j.jour} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-700">{jourCourt(j.jour)}</td>
                    <td className="px-3 py-2 text-right text-gray-800">{j.tickets}</td>
                    <td className="px-3 py-2 text-right text-gray-800">{metres(j.mm)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </CarteReglages>
  );
};

const OngletJournalRecus = ({ avertir }) => (
  <div className="space-y-5">
    <EnTeteSection
      icone={BookOpen}
      titre="Journal du tiroir et du papier"
      sousTitre="Chaque ouverture du tiroir-caisse commandée par KLASYS, chaque reçu imprimé, et le stock de rouleaux"
      accent={ACCENT}
      accentSombre={ACCENT_SOMBRE}
    />
    <JournalDuJour avertir={avertir} />
    <PapierThermique avertir={avertir} />
  </div>
);

export default OngletJournalRecus;
